import React, { useEffect, useState } from "react";
import { Plus, Pin, Trash2, X, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { Button } from "../components/ui/button";

const COLOR_PALETTE = [
  { name: "Amarelo", value: "#FEF3C7" },
  { name: "Verde", value: "#D1FAE5" },
  { name: "Azul", value: "#DBEAFE" },
  { name: "Rosa", value: "#FCE7F3" },
  { name: "Laranja", value: "#FED7AA" },
  { name: "Lilás", value: "#E9D5FF" },
];

const emptyNote = () => ({
  title: "",
  content: "",
  color: COLOR_PALETTE[0].value,
  pinned: false,
});

function formatRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString("en-US", { day: "2-digit", month: "short" });
}

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, title, content, color, pinned}

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/notes");
      setNotes(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao carregar");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function save() {
    if (!editing) return;
    const body = {
      title: editing.title?.trim() || "",
      content: editing.content?.trim() || "",
      color: editing.color || COLOR_PALETTE[0].value,
      pinned: !!editing.pinned,
    };
    if (!body.title && !body.content) {
      toast.error("Escreva um título ou conteúdo");
      return;
    }
    try {
      if (editing.id) {
        await api.patch(`/notes/${editing.id}`, body);
        toast.success("Nota salva");
      } else {
        await api.post(`/notes`, body);
        toast.success("Nota criada");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao salvar");
    }
  }

  async function togglePin(note) {
    try {
      await api.patch(`/notes/${note.id}`, { ...note, pinned: !note.pinned });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function remove(note) {
    if (!window.confirm("Excluir esta nota?")) return;
    try {
      await api.delete(`/notes/${note.id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">
            Lembretes
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Minhas notas</h1>
          <p className="text-sm text-slate-600 mt-1">
            Post-its pessoais — visíveis somente para você.
          </p>
        </div>
        <Button
          data-testid="note-new-btn"
          onClick={() => setEditing(emptyNote())}
          className="bg-amber-500 hover:bg-amber-600"
        >
          <Plus size={16} className="mr-1" /> Nova nota
        </Button>
      </div>

      {loading ? (
        <div className="text-slate-500">Carregando...</div>
      ) : notes.length === 0 ? (
        <div className="cm-card p-8 text-center" data-testid="notes-empty">
          <div className="text-5xl mb-3">📝</div>
          <div className="text-lg font-semibold text-slate-800">
            Nenhuma nota ainda
          </div>
          <div className="text-sm text-slate-500 mt-1">
            Crie sua primeira anotação para guardar lembretes do dia a dia.
          </div>
          <Button
            onClick={() => setEditing(emptyNote())}
            className="mt-4 bg-amber-500 hover:bg-amber-600"
          >
            <Plus size={16} className="mr-1" /> Criar primeira nota
          </Button>
        </div>
      ) : (
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}
          data-testid="notes-grid"
        >
          {notes.map((n) => (
            <NoteCard
              key={n.id}
              note={n}
              onEdit={() => setEditing(n)}
              onPin={() => togglePin(n)}
              onDelete={() => remove(n)}
            />
          ))}
        </div>
      )}

      {editing && (
        <NoteEditor
          note={editing}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      )}
    </AdminLayout>
  );
}

function NoteCard({ note, onEdit, onPin, onDelete }) {
  return (
    <div
      className="rounded-xl p-3 shadow-sm border border-black/5 relative group transition-transform hover:-translate-y-0.5 hover:shadow-md cursor-pointer"
      style={{ background: note.color || "#FEF3C7" }}
      onClick={onEdit}
      data-testid={`note-card-${note.id}`}
    >
      {note.pinned && (
        <Pin
          size={14}
          className="absolute top-2 right-2 text-amber-700 fill-amber-300"
        />
      )}
      <div className="font-bold text-slate-900 text-sm mb-1 pr-4 break-words">
        {note.title || (
          <span className="text-slate-500 italic font-normal">Sem título</span>
        )}
      </div>
      <div className="text-xs text-slate-700 whitespace-pre-wrap break-words max-h-40 overflow-hidden">
        {note.content || (
          <span className="text-slate-400 italic">(vazio)</span>
        )}
      </div>
      <div className="mt-3 flex items-center justify-between text-[10px] text-slate-500">
        <span>{formatRelative(note.updated_at)}</span>
        <div
          className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={onPin}
            data-testid={`note-pin-${note.id}`}
            title={note.pinned ? "Desafixar" : "Fixar no topo"}
            className="p-1 hover:bg-black/10 rounded"
          >
            <Pin size={12} className={note.pinned ? "fill-current" : ""} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            data-testid={`note-delete-${note.id}`}
            title="Excluir"
            className="p-1 hover:bg-black/10 rounded text-red-700"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

function NoteEditor({ note, onChange, onCancel, onSave }) {
  const upd = (k, v) => onChange({ ...note, [k]: v });
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="rounded-xl shadow-2xl max-w-md w-full p-4"
        style={{ background: note.color || "#FEF3C7" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="note-editor"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="font-bold text-slate-900">
            {note.id ? "Editar nota" : "Nova nota"}
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="p-1 hover:bg-black/10 rounded"
            data-testid="note-editor-close"
          >
            <X size={18} />
          </button>
        </div>
        <input
          autoFocus
          value={note.title}
          onChange={(e) => upd("title", e.target.value)}
          placeholder="Título"
          data-testid="note-title-input"
          className="w-full bg-transparent border-b border-black/20 px-1 py-2 font-bold text-slate-900 placeholder:text-slate-500 focus:outline-none focus:border-black/40"
          maxLength={120}
        />
        <textarea
          value={note.content}
          onChange={(e) => upd("content", e.target.value)}
          placeholder="Escreva sua anotação..."
          data-testid="note-content-input"
          rows={8}
          className="w-full bg-transparent px-1 py-2 mt-2 text-sm text-slate-800 placeholder:text-slate-500 focus:outline-none resize-none"
          maxLength={10000}
        />
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-black/10">
          <span className="text-[11px] font-semibold uppercase text-slate-700 mr-1">
            Cor
          </span>
          {COLOR_PALETTE.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => upd("color", c.value)}
              title={c.name}
              data-testid={`note-color-${c.value.replace("#", "")}`}
              className={`w-6 h-6 rounded-full ring-1 ring-black/20 ${
                note.color === c.value ? "ring-2 ring-black scale-110" : ""
              }`}
              style={{ background: c.value }}
            />
          ))}
          <label className="ml-auto inline-flex items-center gap-1 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={!!note.pinned}
              onChange={(e) => upd("pinned", e.target.checked)}
              data-testid="note-pinned-checkbox"
            />
            <Pin size={12} /> Fixar
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button onClick={onSave} data-testid="note-save-btn">
            <Save size={14} className="mr-1" /> Salvar
          </Button>
        </div>
      </div>
    </div>
  );
}
